<template>
    <HeadMetaGame noindex />
    <div
        v-if="game"
        :class="$style.export_game"
    >
        <ExportInitScreen
            v-if="showInitScreen"
            :class="$style.exportInitScreen_override"
            :game="game"
            :categories="gameCatInfo"
            @click="onInitScreenClick"
        />
        <div
            v-else
            :class="$style.iframe_wrapper"
        >
            <iframe
                v-if="isGameFrameVisible"
                ref="iframeRef"
                :class="$style.iframe"
                :title="game.title"
                :src="gameSrc"
            />
            <div
                v-if="!isGameFrameVisible || instreamData.totalAds > 1"
                :class="$style.iframe_fallback"
            />
        </div>
        <InstreamInfo
            v-if="isInstreamInfoShown"
            :class="$style.instream_info_override"
            :secondsLeft="instreamData.secondsLeft"
            :currentAd="instreamData.currentAd"
            :totalAds="instreamData.totalAds"
        />
        <template v-if="showMobileInterface">
            <AdFlipCards
                v-if="showMobileInterface"
                :adMode="mode"
                :class="[$style.ad_block_override, isWidget && $style.ad_block_widget_override]"
            >
                <template #front>
                    <StickyGameBanner
                        v-if="isPortal"
                        :game="game"
                        :categories="gameCatInfo"
                    />
                    <StickyWidgetBanner v-else />
                </template>
            </AdFlipCards>
            <RotateBanner
                v-if="showRotateBanner"
                :orientation="orientation"
                :class="$style.rotateBanner_override"
                @close="onMessageClose"
            />
            <DragnDropClose
                v-if="isGameFrameVisible"
                :mode="mode"
                :startPosition="40"
                :startLandscapePosition="10"
                :class="isWidget && $style.close_widget_override"
                @click="closeGame"
            />
        </template>
        <BrandPreloader
            v-if="showBrandPreloader"
            :hasError="brandPreloaderHasError"
            :adType="brandPreloaderAdType"
            :isPortal
            @close="resetPreloader"
        />
    </div>
    <ErrorContent
        v-else-if="pageStatus !== 200"
        noRandomGame
        :type="pageStatus"
    />
</template>
<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { Ref, computed, onBeforeUnmount, onMounted, onServerPrefetch, ref, shallowRef } from 'vue'
import { useRoute } from 'vue-router'

import ErrorContent from '@/components_new/ErrorContent/ErrorContent.vue'
import { useConfig } from '@/composables/config'
import { DEFAULT_CLIDS } from '@/constants/general'
import { useI18nAddon } from '@/i18n/i18n'
import { OutOfPageAdType } from '@/modules/adv'
import { createCloudSave, getCloudSaveApi } from '@/modules/cloud-saves'
import { GameAttributes, createInGamePayments } from '@/modules/in-game-payments'
import { MainFrameService } from '@/modules/main-frame'
import { GameAdHandler } from '@/modules/main-frame/ads/game-ad-handler'
import { createSafeStorage } from '@/modules/safe-storage'
import { UserService } from '@/modules/user-service'
import { addRedirectCounter } from '@/router/query-params'
import { useAdsStore } from '@/store/ads-store'
import { type PageStatus, useAppStore } from '@/store/app-store'
import { useGamePageStore } from '@/store/game-store'
import { useLoggerStore } from '@/store/logger'
import { useMyGamesStore } from '@/store/my-games/my-games-store'
import { zipMyGame } from '@/store/my-games/utils'
import { useUserStore } from '@/store/user-store/user-store'
import type { InstreamData } from '@/types'
import { getConfigByClid } from '@/utils/clid-helpers'
import { timer } from '@/utils/helpers'
import safeURLParse from '@/utils/safeURLParse'

import AdFlipCards from './components/AdFlipCards.vue'
import BrandPreloader from './components/BrandPreloader/BrandPreloader.vue'
import DragnDropClose from './components/DragnDropClose.vue'
import ExportInitScreen from './components/ExportInitScreen.vue'
import HeadMetaGame from './components/HeadMetaGame.vue'
import InstreamInfo from './components/InstreamInfo/InstreamInfo.vue'
import RotateBanner, { Orientation } from './components/RotateBanner/RotateBanner.vue'
import StickyGameBanner from './components/StickyGameBanner.vue'
import StickyWidgetBanner from './components/StickyWidgetBanner.vue'

const PAYLOAD_PARAM_NAME = 'payload'
const ADD_GAME_TIME_MS = 15_000 // 15 sec
const MAX_PRELOADER_DISPLAY_MS = 10_000 // 10 sec

const i18NAddon = useI18nAddon()
const addMyGameTimeOut = ref<number | undefined>(ADD_GAME_TIME_MS)
const gamePageStore = useGamePageStore()
const myGamesStore = useMyGamesStore()
const appStore = useAppStore()
const { pageStatus, isWidget, isPortal } = storeToRefs(appStore)
const { game, gameCatInfo } = storeToRefs(gamePageStore)

const route = useRoute()
const rotateBannerClosed = ref(false)
const isGameFrameVisible = ref(false)
const gameSrc = ref('')
const iframeRef = ref<HTMLIFrameElement>()

const instreamData = ref({
    isVisible: false,
    secondsLeft: 0,
    currentAd: 1,
    totalAds: 1,
})

const showBrandPreloader = ref(false)
const brandPreloaderHasError = ref(false)
const brandPreloaderAdType = ref<OutOfPageAdType | null>(null)

const showInitScreen = ref(!getConfigByClid(appStore.clid).isInitGameScreenHidden)

const gameAdHandler = shallowRef<GameAdHandler>()
const mainFrameService = ref<MainFrameService>()
const adsStore = useAdsStore()
const loggerStore = useLoggerStore()
const userStore = useUserStore()

const showMobileInterface = computed(
    () => appStore.platform !== 'desktop' && game.value && (isPortal.value || isWidget.value),
)

const isInstreamInfoShown = computed(
    () => instreamData.value.isVisible && (!isPortal.value || appStore.platform !== 'desktop'),
)

const mode = computed(() => (isWidget.value ? 'widget' : 'portal'))

const showRotateBanner = computed(
    () => !rotateBannerClosed.value && isGameFrameVisible.value && game.value?.source !== 'playhop',
)

async function updateData(routeParams: string) {
    if (!game || !game.value || game.value.hru !== routeParams) {
        gamePageStore.resetGameData()
        await gamePageStore.setGame(routeParams)
    }
}

function ping() {
    loggerStore.logEvent({
        event: 'custom_event',
        eventName: 'ping',
    })
}

function waitToAddMyGame() {
    if (addMyGameTimeOut.value === undefined) {
        return
    }
    addMyGameTimeOut.value -= VUE_APP_GAME_PING_INTERVAL_MS
    if (addMyGameTimeOut.value <= 0) {
        addMyGameTimeOut.value = undefined
        myGamesStore.update(zipMyGame(game.value!))
    }
}

const playtime = ref(0)
const sent3MinPlaygame = ref(false)
const sent5MinPlaygame = ref(false)
const sent10MinPlaygame = ref(false)
function sendPlaygameEventOnThreshold(flagRef: Ref<boolean>, thresholdMin: 3 | 5 | 10) {
    const min = 1000 * 60
    if (playtime.value >= thresholdMin * min && !flagRef.value) {
        flagRef.value = true
        loggerStore.logEvent({
            event: 'custom_event',
            eventName: `playgame_${thresholdMin}min`,
        })
    }
}
function sendPlaygameEvents() {
    playtime.value += VUE_APP_GAME_PING_INTERVAL_MS
    sendPlaygameEventOnThreshold(sent3MinPlaygame, 3)
    sendPlaygameEventOnThreshold(sent5MinPlaygame, 5)
    sendPlaygameEventOnThreshold(sent10MinPlaygame, 10)
}

const { VUE_APP_GAME_PING_INTERVAL_MS } = useConfig()
const pingIntervalRef = ref<ReturnType<typeof setInterval>>()

const handleCloseGame = shallowRef<() => void>()
const sendInstreamData = shallowRef<(data: InstreamData) => void>()

function closeGame() {
    handleCloseGame.value?.()
}

const orientation = computed(() => {
    const result: Orientation[] = []

    if (game.value?.vertical) {
        result.push('vertical')
    }
    if (game.value?.horizontal) {
        result.push('horizontal')
    }
    return result
})

function onMessageClose() {
    rotateBannerClosed.value = true
}

function blurGameFrame() {
    iframeRef.value?.blur()
}

function updateInstreamData(data: Partial<InstreamData>) {
    const newValue = {
        ...instreamData.value,
        ...data,
    }

    instreamData.value = newValue
    sendInstreamData.value?.(newValue)
}

function resetInstreamWrapper() {
    updateInstreamData({
        isVisible: false,
        secondsLeft: 0,
        currentAd: 1,
        totalAds: 1,
    })
}

function openPreloader(adType: OutOfPageAdType) {
    if (!showBrandPreloader.value) {
        showBrandPreloader.value = true
        brandPreloaderHasError.value = false
        brandPreloaderAdType.value = adType

        setTimeout(() => {
            if (showBrandPreloader.value && !brandPreloaderHasError.value) {
                resetPreloader()
                startGameLoading()

                loggerStore.logEvent({
                    event: 'custom_event',
                    eventName: 'game_preloader_timeout',
                    waterfallIndex: adsStore.adService?.currentOutOfPageAdWaterfallIndex,
                })
            }
        }, MAX_PRELOADER_DISPLAY_MS)
    }
}

function resetPreloader() {
    showBrandPreloader.value = false
    brandPreloaderHasError.value = false
    brandPreloaderAdType.value = null
}

function startGameLoading() {
    if (!isGameFrameVisible.value) {
        isGameFrameVisible.value = true

        loggerStore.logEvent({
            event: 'custom_event',
            eventName: 'game',
            action: 'init',
        })
    }
}

onMounted(async () => {
    if (pageStatus.value !== 200) {
        return
    }

    if (!game.value) {
        // dev-mode
        await updateData(route.params.game as string)
    }

    // TODO: make this more transparent
    // but should never happen, otherwise `new URL(game.value.game_url)` would cause errors
    if (!game.value) {
        console.error('Error: No game found')
        return
    }

    if (!adsStore.adService) {
        console.error('Error: No ad service was passed to the main frame')
        return
    }

    adsStore.updateTargeting({ content_category: game.value.categories, provider: game.value.source || '' })

    const payload = route.query[PAYLOAD_PARAM_NAME] || ''

    const url = safeURLParse(game.value.game_url)

    if (!url) {
        return
    }

    // this logic is only needed for playhop — they give us statistic based on clids
    // but the clids should be one of given by them, and not our default "direct" or "direct-test"
    if (appStore.clid && !Object.values(DEFAULT_CLIDS).includes(appStore.clid)) {
        url.searchParams.set('clid', appStore.clid as string)
    }
    if (payload) {
        url.searchParams.set('payload', payload as string)
    }

    // Experiment serve games from .net domain to avoid Web Vitals effect on playgama.com
    if (url.hostname.endsWith('games.playgama.com')) {
        url.hostname = url.hostname.replace('games.playgama.com', 'games.playgama.net')
    }

    loggerStore.logEvent({
        event: 'custom_event',
        eventName: 'game_opened',
    })

    let { authorizeUser, getUser, chargeUser } = userStore
    let attributes: GameAttributes = {
        clid: appStore.clid,
        widgetId: appStore.widgetId,
        userAcquisitionParams: appStore.userAcquisitionParams,
    }

    if (isPortal.value || isWidget.value) {
        const parentExportApi = window.parent.exportApi
        if (parentExportApi) {
            authorizeUser = parentExportApi.authorizeUser
            getUser = parentExportApi.getUser
            chargeUser = parentExportApi.chargeUser
            sendInstreamData.value = parentExportApi.updateInstreamData
            handleCloseGame.value = parentExportApi.closeGame
            attributes = parentExportApi.getGameAttributes()
        }
    }
    gameSrc.value = url.href

    const cloudSaveApi = getCloudSaveApi(game.value.id)

    if (!adsStore.adService) {
        return
    }

    gameAdHandler.value = new GameAdHandler(adsStore.adService, loggerStore.logEvent, appStore.platform)
    mainFrameService.value = new MainFrameService({
        currentLanguage: i18NAddon.getCurrentLanguage(),
        gameAdHandler: gameAdHandler.value!,
        inGamePaymentsMessageHandler: createInGamePayments(game.value.hru, chargeUser, attributes),
        storageMessageHandler: createSafeStorage(game.value.hru, getUser, cloudSaveApi),
        cloudSaveMessageHandler: createCloudSave(game.value.id, game.value.hru),
        userService: new UserService({ authorizeUser, getUser }),
        onLogin: async () => {
            if (!game.value) {
                return
            }
            const api = getCloudSaveApi(game.value.id)

            const save = await api.getState()

            if (Object.keys(save).length === 0) {
                return
            }

            addRedirectCounter(url)
            gameSrc.value = url.href
        },
        onGameStart: () => {
            loggerStore.logEvent({
                event: 'custom_event',
                eventName: 'game_bridge_loaded',
            })
        },
    })
    mainFrameService.value?.addEventListener('adStateChanged', async (event) => {
        const data = event.detail

        switch (data.action) {
            case 'start':
                openPreloader(data.adType)
                blurGameFrame()
                break
            case 'closed':
                resetInstreamWrapper()
                resetPreloader()

                if (data.adType === 'interstitial_preroll') {
                    startGameLoading()
                }

                break
            case 'empty':
            case 'errored':
                resetInstreamWrapper()

                if (showBrandPreloader.value) {
                    const MIN_LOADER_DISPLAY_MS = 1200
                    await timer(MIN_LOADER_DISPLAY_MS)
                    brandPreloaderHasError.value = data.adType !== 'interstitial_preroll'
                }

                if (data.adType === 'interstitial_preroll') {
                    resetPreloader()
                    startGameLoading()
                }
                break
            case 'timer':
                if (
                    data.secondsLeft !== undefined &&
                    data.secondsLeft >= 0 &&
                    data.secondsLeft !== instreamData.value.secondsLeft
                ) {
                    updateInstreamData({
                        isVisible: true,
                        secondsLeft: data.secondsLeft,
                    })
                }
                break
            case 'opened':
                resetPreloader()
                break
            case 'update':
                updateInstreamData({
                    currentAd: data.currentAd || 1,
                    totalAds: data.totalAds || 1,
                })
                break
            default:
            // do nothing
        }
    })

    if (!showInitScreen.value) {
        startGame()
    }
})

function startGame() {
    showInitScreen.value = false

    pingIntervalRef.value = setInterval(() => {
        if (!document.hidden && document.hasFocus()) {
            ping()
            waitToAddMyGame()
            sendPlaygameEvents()
        }
    }, VUE_APP_GAME_PING_INTERVAL_MS)

    openPreloader('interstitial_preroll')

    adsStore.adService?.serviceReadyPromise.then(() => {
        gameAdHandler.value?.showPreroll()
    })
}

function onInitScreenClick() {
    showInitScreen.value = false

    startGame()

    loggerStore.logEvent({
        event: 'custom_event',
        eventName: 'export-init-click',
    })
}

onBeforeUnmount(() => {
    mainFrameService.value?.destroy()
    clearInterval(pingIntervalRef.value || 0)
})

onServerPrefetch(async () => {
    try {
        await gamePageStore.setGame(route.params.game as string)
    } catch (e) {
        // no throwing here, we handle errors manually
        if (typeof e === 'number') {
            appStore.setPageStatus(e as PageStatus)
        } else {
            appStore.setPageStatus(500)
        }
    }
})
</script>
<style module>
.rotateBanner_override {
    position: absolute;
    top: 14px;
    left: 50%;
    transform: translate(-50%, 0);
}
.export_game {
    position: relative;
    display: grid;
    grid-template-rows: 1fr auto;
    padding-bottom: env(safe-area-inset-bottom);
    padding-top: env(safe-area-inset-top);
    height: 100svh;
    overflow: hidden;
}
.exportInitScreen_override,
.iframe_wrapper {
    grid-area: 1 / 1;
}

.iframe_wrapper {
    position: relative;
}
.iframe,
.iframe_fallback {
    width: 100%;
    height: 100%;
}
.iframe_fallback {
    background-color: var(--black);
    position: absolute;
    top: 0;
    left: 0;
}
.ad_block_override {
    width: 100%;
    height: 50px;
}

.instream_info_override {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    color: white;
    border-bottom: 1px solid var(--carbon-400);
    padding-left: 16px;
}

@media (--tablet-plus) {
    .ad_block_override {
        height: 90px;
    }

    .close_widget_override,
    .ad_block_widget_override {
        display: none;
    }
}

@media (--mobile-landscape), (--desktop-small-plus) {
    .ad_block_override {
        display: none;
    }

    .export_game {
        padding-bottom: 0;
    }
}
</style>
